import {useCallback, type RefObject} from 'react'

interface Focusable {
  focus(): void
}

/**
 * If the target container unmounts while focus is inside of (or on) it, this hook will return focus to the target.
 * @param returnFocusRef Ref attached to the item to return focus to. **Must be focusable and remain (or become)
 * mounted when the container unmounts.**
 */
export function useReturnFocus(returnFocusRef: RefObject<Focusable | null>) {
  const containerRef = useCallback(
    (container: HTMLElement | null) => {
      return () => {
        // Timeout allows for cases where the return focus target renders in place of the container
        if (container?.contains(document.activeElement)) setTimeout(() => returnFocusRef.current?.focus())
      }
    },
    [returnFocusRef],
  )

  return {
    /**
     * Attach this ref the container around all the focuseable elements (or the focuseable target itself if there is
     * only one). This container **must** be conditionally rendered; focus will only be returned when this item is
     * unmounted.
     */
    containerRef,
  }
}
